import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useDashboard } from '@/context/DashboardContext';
import { IMPACT, TREND, PROGRAMS, PROGRAM_LIST, TODAY, programColor, programLabel } from '@/data/seed';
import { PIPELINE, OVER_ALLOC } from '@/data/pipeline';
import { cr, crFull, fmtIN, fmtDate, daysFrom, shortIN } from '@/lib/fmt';
import KpiCard from '@/components/KpiCard';
import { Chip, ProgramChip } from '@/components/UiBits';
import { PROGRAM_CARD, KPI } from '@/constants/testIds';
import { Users, ReceiptText, Wallet, TrendingUp, GitFork, School, GraduationCap, HeartPulse, MapPin, Award } from 'lucide-react';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend
} from 'recharts';

export default function Overview() {
  const nav = useNavigate();
  const { fy, donors, grants, totals, PROGRAMS: P } = useDashboard();

  const utilPct = totals.recd > 0 ? (totals.util / totals.recd) * 100 : 0;
  const overdue = grants.filter((g) => g.uc === 'Overdue');
  const due30 = grants.filter((g) => g.uc === 'Due' && daysFrom(g.report, TODAY) <= 30);
  const ending2q = grants.filter((g) => daysFrom(g.end, TODAY) <= 180);
  const openPipe = PIPELINE.filter((p) => p.stage !== 'converted');
  const openPipeVal = openPipe.reduce((s, p) => s + p.val, 0);

  const donorById = (id) => donors.find((d) => d.id === id);
  const donorCommitted = (d) => grants.filter((g) => g.donor === d.id).reduce((s, g) => s + g.sanct, 0);
  const donorsSorted = [...donors].sort((a, b) => donorCommitted(b) - donorCommitted(a));
  const top3Share = donorsSorted.slice(0, 3).reduce((s, d) => s + donorCommitted(d), 0) / (totals.sanct || 1) * 100;
  const fcraShare = donors.filter((d) => d.fcra).reduce((s, d) => s + donorCommitted(d), 0) / (totals.sanct || 1) * 100;

  // Chart datasets
  const trendData = TREND.labels.map((l, i) => ({ fy: l, Committed: TREND.committed[i], Utilised: TREND.utilised[i] }));
  const mixData = PROGRAM_LIST.map((k) => ({
    name: P[k].name,
    value: +grants.filter((g) => g.program === k).reduce((s, g) => s + g.sanct, 0).toFixed(1),
    color: P[k].color,
  }));
  const top5 = donorsSorted.slice(0, 5).map((d) => ({ name: d.name, value: +donorCommitted(d).toFixed(1) }));
  const fcraVal = donors.filter((d) => d.fcra).reduce((s, d) => s + donorCommitted(d), 0);
  const fcraData = [
    { name: 'Non-FCRA (domestic CSR)', value: +(totals.sanct - fcraVal).toFixed(1), color: '#FFC20E' },
    { name: 'FCRA (foreign source)',   value: +fcraVal.toFixed(1),                  color: '#1F2937' },
  ];

  return (
    <>
      <div className="hero">
        <div className="overline">Sahaya Foundation · Where hope meets action</div>
        <h2>One grantee, four pillars, {fmtIN(IMPACT.totalBenef)} lives.</h2>
        <p>
          Sahaya&apos;s donor portfolio, fundraising pipeline and programme delivery — the complete picture
          for the CEO and Board of Trustees.
        </p>
        <span className="viewing">Viewing <b>{fy}</b> · data as of {fmtDate(TODAY.toISOString())}</span>
      </div>

      <div className="nudge-row">
        <div className="nudge nudge-danger">
          <span><b>{overdue.length} donor {overdue.length === 1 ? 'report' : 'reports'} overdue</b>
            {overdue.length > 0 && ' — ' + overdue.map((g) => donorById(g.donor)?.name).filter(Boolean).join(' · ')}</span>
          <span className="nudge-cta" onClick={() => nav('/grants')}>View overdue →</span>
        </div>
        <div className="nudge nudge-warning">
          <span><b>{due30.length} reports due in 30 days</b> across {new Set(due30.map((g) => g.donor)).size} donors</span>
          <span className="nudge-cta" onClick={() => nav('/grants')}>Compliance calendar →</span>
        </div>
        <div className="nudge nudge-info">
          <span><b>{cr(ending2q.reduce((s, g) => s + g.sanct, 0))} in grants end within 2 quarters</b> — renewal conversations needed</span>
          <span className="nudge-cta" onClick={() => nav('/donors')}>Renewal view →</span>
        </div>
        <div className="nudge nudge-warning">
          <span><b>{OVER_ALLOC.length} staff over-allocated across grants</b> (&gt;100% salary charged) — audit exposure</span>
          <span className="nudge-cta" onClick={() => nav('/resources')}>Resource view →</span>
        </div>
      </div>

      <div className="overline mb-2.5">Funding position</div>
      <div className="kpi-row">
        <KpiCard testId={KPI.activeDonors} icon={Users}        iconCls="ic-gold"  value={donors.length}          label="Active Donors"           delta={['up', '+2']}   last="Last FY: 12 donors" />
        <KpiCard testId={KPI.activeGrants} icon={ReceiptText}  iconCls="ic-blue"  value={grants.length}          label="Active Grants"           delta={['up', '+3']}   last="Last FY: 11 grants" />
        <KpiCard testId={KPI.committed}    icon={Wallet}       iconCls="ic-gold"  value={cr(totals.sanct)}       label="Committed (Active Portfolio)" delta={['up', '23%']}  last="Last FY: ₹28.9 Cr" title={crFull(totals.sanct)} />
        <KpiCard testId={KPI.received}     icon={Wallet}       iconCls="ic-green" value={cr(totals.recd)}        label="Funds Received"          delta={['flat', ((totals.recd / totals.sanct) * 100).toFixed(0) + '% of committed']} last="Last FY: ₹23.6 Cr" title={crFull(totals.recd)} />
        <KpiCard testId={KPI.utilised}     icon={TrendingUp}   iconCls="ic-green" value={cr(totals.util)}        label="Utilised"                delta={['up', utilPct.toFixed(0) + '% of received']} last="Last FY: ₹18.9 Cr" title={crFull(totals.util)} />
        <KpiCard testId={KPI.pipeline}     icon={GitFork}      iconCls="ic-amber" value={cr(openPipeVal)}        label="Open Pipeline (FY 25-26)" delta={['up', openPipe.length + ' proposals']} last="Same time last FY: ₹18.2 Cr" />
      </div>

      <div className="overline mb-2.5">Impact position · FY 24-25 actuals</div>
      <div className="kpi-row">
        <KpiCard testId={KPI.totalBenef} icon={Users}         iconCls="ic-gold"  value={shortIN(IMPACT.totalBenef)} label="Total Beneficiaries" delta={['up','19%']} last="Last FY: 16.3 L" title={fmtIN(IMPACT.totalBenef)} />
        <KpiCard                          icon={MapPin}        iconCls="ic-blue"  value={IMPACT.states}              label={`States + ${IMPACT.uts} UTs`} />
        <KpiCard testId={KPI.districts}   icon={MapPin}        iconCls="ic-blue"  value={IMPACT.districts}           label="Districts"          delta={['up','14 added']} />
        <KpiCard testId={KPI.centres}     icon={School}        iconCls="ic-green" value="735"                         label="Mission Ed. Centres" delta={['up','5% vs FY 23-24']} />
        <KpiCard                          icon={GraduationCap} iconCls="ic-amber" value="12,660+"                     label="Govt. Schools Supported" delta={['up','8% vs FY 23-24']} />
        <KpiCard                          icon={HeartPulse}    iconCls="ic-red"   value="12.9 L+"                     label="Healthcare Reach"        delta={['up','23% vs FY 23-24']} />
      </div>

      <div className="grid-23">
        <div className="card">
          <div className="card-head">
            <div>
              <div className="card-title">Funding Trend — Committed vs Utilised</div>
              <div className="card-sub">₹ crore · active portfolio by financial year</div>
            </div>
          </div>
          <div style={{ height: 260 }}>
            <ResponsiveContainer>
              <BarChart data={trendData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" vertical={false} />
                <XAxis dataKey="fy" tick={{ fontSize: 11, fill: '#6B7280' }} />
                <YAxis tick={{ fontSize: 11, fill: '#6B7280' }} tickFormatter={(v) => `₹${v}Cr`} />
                <Tooltip formatter={(v) => cr(v)} contentStyle={{ borderRadius: 8, border: '1px solid #E5E7EB', fontSize: 12 }} />
                <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="Committed" fill="#FFC20E" radius={[6, 6, 0, 0]} maxBarSize={44} />
                <Line dataKey="Utilised" type="monotone" stroke="#1F2937" strokeWidth={2} dot={{ r: 4 }} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="card">
          <div className="card-head">
            <div>
              <div className="card-title">Funding Mix by Program</div>
              <div className="card-sub">Committed ₹ · active grants</div>
            </div>
          </div>
          <div style={{ height: 260 }}>
            <ResponsiveContainer>
              <PieChart>
                <Pie data={mixData} innerRadius={54} outerRadius={90} dataKey="value" paddingAngle={2}>
                  {mixData.map((e, i) => <Cell key={i} fill={e.color} stroke="#fff" strokeWidth={2} />)}
                </Pie>
                <Tooltip formatter={(v) => cr(v)} contentStyle={{ borderRadius: 8, border: '1px solid #E5E7EB', fontSize: 12 }} />
                <Legend iconType="circle" wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="sec-title">Trustee risk lens</div>
      <div className="grid-3">
        <div className="card">
          <div className="card-head">
            <div>
              <div className="card-title">Donor Concentration</div>
              <div className="card-sub">Share of committed funds — top donors</div>
            </div>
          </div>
          <div style={{ height: 210 }}>
            <ResponsiveContainer>
              <BarChart data={top5} layout="vertical" margin={{ top: 5, right: 20, left: 8, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 11, fill: '#6B7280' }} tickFormatter={(v) => `₹${v}`} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: '#374151' }} width={140} />
                <Tooltip formatter={(v) => cr(v)} contentStyle={{ borderRadius: 8, border: '1px solid #E5E7EB', fontSize: 12 }} />
                <Bar dataKey="value" radius={[0, 5, 5, 0]} maxBarSize={20}>
                  {['#08519C', '#2171B5', '#4292C6', '#6BAED6', '#9ECAE1'].map((c, i) => <Cell key={i} fill={c} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-2.5 text-[12.5px] text-[color:var(--gray-500)]">
            Top 3 donors ={' '}
            <b style={{ color: top3Share > 50 ? 'var(--red-600)' : 'var(--amber-600)' }}>{top3Share.toFixed(1)}%</b>
            {' '}of portfolio {top3Share > 50 ? '— above the 50% comfort line' : '— within the 50% comfort line'}
          </div>
        </div>

        <div className="card">
          <div className="card-head">
            <div>
              <div className="card-title">FCRA vs Non-FCRA</div>
              <div className="card-sub">Committed ₹ by regulatory route</div>
            </div>
          </div>
          <div style={{ height: 210 }}>
            <ResponsiveContainer>
              <PieChart>
                <Pie data={fcraData} innerRadius={54} outerRadius={82} dataKey="value" paddingAngle={2}>
                  {fcraData.map((e, i) => <Cell key={i} fill={e.color} stroke="#fff" strokeWidth={2} />)}
                </Pie>
                <Tooltip formatter={(v) => cr(v)} contentStyle={{ borderRadius: 8, border: '1px solid #E5E7EB', fontSize: 12 }} />
                <Legend iconType="circle" wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-2.5 text-[12.5px] text-[color:var(--gray-500)]">
            {fcraShare.toFixed(1)}% of funds flow through <b>FCRA</b> —{' '}
            {donors.filter((d) => d.fcra).map((d) => d.name).join(', ')}
          </div>
        </div>

        <div className="card">
          <div className="card-head">
            <div>
              <div className="card-title">Renewal Exposure</div>
              <div className="card-sub">Grants ending within 2 quarters</div>
            </div>
          </div>
          <div style={{ maxHeight: 262, overflowY: 'auto' }}>
            {ending2q.length === 0 && <div className="empty-sub">No grants ending in the next two quarters.</div>}
            {[...ending2q].sort((a, b) => new Date(a.end) - new Date(b.end)).map((g) => {
              const d = donorById(g.donor);
              return (
                <div key={g.id} className="flex items-center justify-between gap-2 py-2 border-b border-[color:var(--gray-100)] last:border-b-0">
                  <div className="min-w-0">
                    <div className="text-[13px] font-semibold text-[color:var(--gray-800)] truncate">{d?.name || 'Unknown'}</div>
                    <div className="text-[11px] text-[color:var(--gray-400)] truncate">{g.name} · ends {fmtDate(g.end)}</div>
                  </div>
                  <div className="text-right flex-none">
                    <div className="text-[13px] font-bold text-[color:var(--gray-900)]" title={crFull(g.sanct)}>{cr(g.sanct)}</div>
                    <Chip kind={d?.renewal || 'Multi-year'}>{d?.renewal || 'Multi-year'}</Chip>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="sec-title">Four pillars — drill in</div>
      <div className="grid-2" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))' }}>
        {PROGRAM_LIST.map((k) => {
          const p = P[k];
          const gs = grants.filter((g) => g.program === k);
          const totalSanct = gs.reduce((s, g) => s + g.sanct, 0);
          const totalBenef = gs.reduce((s, g) => s + g.benef, 0);
          const donorCount = new Set(gs.map((g) => g.donor)).size;
          const stats = [[cr(totalSanct), 'Committed'], [gs.length, 'Active grants'], [shortIN(totalBenef), 'Beneficiaries'], [donorCount, 'Donors']];
          return (
            <div key={k} data-testid={PROGRAM_CARD(k)} className="program-card" style={{ borderTopColor: p.color }}>
              <div className="pc-over" style={{ color: p.color }}>Pillar</div>
              <h3>{p.name}</h3>
              <div className="pc-desc">{p.desc}</div>
              <div className="pc-stats">
                {stats.map((s, i) => (
                  <div key={i} className="pc-stat">
                    <div className="v">{s[0]}</div>
                    <div className="l">{s[1]}</div>
                  </div>
                ))}
              </div>
              <div className="pc-tags">
                {p.tags.map((t) => <span key={t} className="pc-tag">{t}</span>)}
              </div>
            </div>
          );
        })}
      </div>

      <div className="card">
        <div className="card-head">
          <div>
            <div className="card-title">Next Donor Obligations</div>
            <div className="card-sub">Reports &amp; utilisation certificates due soonest</div>
          </div>
          <span className="view-all" onClick={() => nav('/grants')}>Full compliance calendar →</span>
        </div>
        <div className="tbl-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <th>Donor · Grant</th><th>Program</th><th>Due Date</th><th className="ctr">UC Status</th><th className="num">Grant Value</th>
              </tr>
            </thead>
            <tbody>
              {[...grants].sort((a, b) => new Date(a.report) - new Date(b.report)).slice(0, 6).map((g) => {
                const d = donorById(g.donor);
                const dd = daysFrom(g.report, TODAY);
                return (
                  <tr key={g.id}>
                    <td>
                      <span className="ent">{d?.name}</span>
                      <span className="sub">{g.name}</span>
                    </td>
                    <td><ProgramChip program={g.program} PROGRAMS={P} /></td>
                    <td>
                      {fmtDate(g.report)}
                      <span className="text-[11px] ml-1" style={{ color: dd < 0 ? 'var(--red-600)' : 'var(--gray-400)' }}>
                        ({dd < 0 ? Math.abs(dd) + 'd overdue' : 'in ' + dd + 'd'})
                      </span>
                    </td>
                    <td className="ctr"><Chip kind={g.uc}>{g.uc}</Chip></td>
                    <td className="num" title={crFull(g.sanct)}>{cr(g.sanct)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
